import * as React from "react"
import { cn } from "../../lib/utils"
import { Check, X } from "lucide-react"

export interface Option {
  value: string;
  label: string;
  tags?: string[];
}

interface MultiSelectProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  ({ className, options, value, onChange, placeholder = "Select...", disabled, ...props }, ref) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");
    const containerRef = React.useRef<HTMLDivElement>(null);

    // Handle click outside to close dropdown
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = options.filter(option => {
      if (!option || !option.label) return false;
      const searchLower = searchTerm.toLowerCase();
      return option.label.toLowerCase().includes(searchLower) ||
        option.tags?.some(tag => tag?.toLowerCase()?.includes(searchLower));
    });

    const handleToggleOption = (optionValue: string) => {
      const newValue = value.includes(optionValue)
        ? value.filter(v => v !== optionValue)
        : [...value, optionValue];
      onChange(newValue);
    };

    const handleRemoveValue = (optionValue: string) => {
      onChange(value.filter(v => v !== optionValue));
    };

    return (
      <div ref={containerRef} className={cn("relative", className)} {...props}>
        {/* Selected items display */}
        <div
          className={cn(
            "min-h-[38px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm",
            "focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50",
            disabled && "bg-gray-100 cursor-not-allowed",
            className
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="flex flex-wrap gap-1">
            {value.length > 0 ? (
              value.map(v => {
                const option = options.find(o => o.value === v);
                return option ? (
                  <span
                    key={v}
                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-sm"
                  >
                    {option.label}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveValue(v);
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ) : null;
              })
            ) : (
              <span className="text-gray-500">{placeholder}</span>
            )}
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && !disabled && (
          <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg">
            <div className="p-2">
              <input
                type="text"
                className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-primary focus:outline-none"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <ul className="max-h-60 overflow-auto py-1">
              {filteredOptions.map((option) => (
                <li
                  key={option.value}
                  className={cn(
                    "flex items-center px-3 py-2 text-sm cursor-pointer",
                    "hover:bg-gray-100"
                  )}
                  onClick={() => handleToggleOption(option.value)}
                >
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "h-4 w-4 rounded border flex items-center justify-center",
                      value.includes(option.value) ? "border-primary bg-primary" : "border-gray-300"
                    )}>
                      {value.includes(option.value) && (
                        <Check className="h-3 w-3 text-white" />
                      )}
                    </div>
                    {option.label}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
);

MultiSelect.displayName = "MultiSelect";

export { MultiSelect };